import { KEYBOARD_SHORTCUT_ACTION_NAME } from '@frameio/utils/hooks/useHotKeys/constants';
import type { EventContext } from './events';
import { emitEvent } from './events';

/**
 * Shared RUM action name for every keyboard-shortcut invocation. A single
 * constant name keeps all shortcuts filterable as one facet (`@action.name`)
 * while `shortcut_id` in the context allows per-shortcut breakdowns.
 */
export { KEYBOARD_SHORTCUT_ACTION_NAME };

export type KeyboardShortcutActionInfo = {
  /** Stable dot-namespaced command id, e.g. `viewing.togglePlaying`. */
  id: string;
  displayLabel?: string;
  displayCategory?: string;
  displayKeys?: string;
  panel?: string;
};

/**
 * Single entry point for reporting a keyboard-shortcut invocation to Datadog RUM
 * as a custom action.
 *
 * Correlating these actions with the INP / long tasks / LoAF they cause is not
 * yet supported by the built-in RUM data model: custom actions are point-in-time
 * markers, while those vitals are attributed to the view rather than an action.
 * When a correlation approach is available, wire it in here so every call site
 * benefits without further changes.
 */
export function captureKeyboardShortcutAction(
  info: KeyboardShortcutActionInfo,
  event?: KeyboardEvent,
  context: EventContext = {}
): void {
  // Ignore OS key-repeat from a held-down key so one press yields one action.
  if (event?.repeat) {
    return;
  }

  const shortcutContext = Object.fromEntries(
    Object.entries({
      shortcut_id: info.id,
      shortcut_label: info.displayLabel,
      shortcut_category: info.displayCategory,
      shortcut_keys: info.displayKeys,
      shortcut_panel: info.panel,
    }).filter(([, value]) => value !== undefined)
  );

  emitEvent(KEYBOARD_SHORTCUT_ACTION_NAME, {
    ...shortcutContext,
    ...context,
  });
}
